import React, { useState, useEffect } from 'react';
import { X, Save, Activity, GitBranch, MousePointer2, FileText } from 'lucide-react';

interface PipeModalProps {
    pipe: any;
    onClose: () => void;
    onUpdate: (id: number, payload: any) => void;
    role: string;
    onStartPickingParent: () => void;
    parentId: number | null;
    isPicking: boolean;
}

const PipeModal: React.FC<PipeModalProps> = ({
                                                 pipe, onClose, onUpdate, role, onStartPickingParent, parentId, isPicking
                                             }) => {
    const [name, setName] = useState(pipe.name || '');
    const [description, setDescription] = useState(pipe.description || '');
    const [isSp, setIsSp] = useState(pipe.is_sp || false);
    const canEdit = role === 'admin' || role === 'designer' || role === 'superadmin';

    useEffect(() => {
        setName(pipe.name || '');
        setDescription(pipe.description || '');
        setIsSp(pipe.is_sp || false);
    }, [pipe]);

    // Tryb wybierania rodzica na mapie
    if (isPicking) {
        return (
            <div style={{
                position: 'fixed', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
                backgroundColor: '#1a1a1a', padding: '15px 25px', borderRadius: '30px',
                border: '2px solid #4f46e5', color: 'white', zIndex: 4000,
                display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
            }}>
                <MousePointer2 size={20} className="animate-pulse" />
                <span style={{ fontWeight: 600 }}>Kliknij w rurę nadrzędną na mapie...</span>
            </div>
        );
    }

    return (
        <div 
            className="pipe-modal-container"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Activity size={16} color="#3b82f6" />
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{name || `Rura #${pipe.id}`}</h3>
                </div>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onClose();
                    }}
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        borderRadius: '6px'
                    }}
                    title="Zamknij okno"
                >
                    <X size={18} />
                </button>
            </div>

            <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <span style={{
                    padding: '3px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 'bold',
                    backgroundColor: pipe.status === 'open' ? '#22c55e33' : '#ef444433',
                    color: pipe.status === 'open' ? '#22c55e' : '#ef4444',
                    border: `1px solid ${pipe.status === 'open' ? '#22c55e' : '#ef4444'}`
                }}>
                    {pipe.status === 'open' ? 'PRZEPŁYW OK' : 'ZAMKNIĘTA'}
                </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {/* Pole: Nazwa */}
                <div>
                    <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>Nazwa / Oznaczenie</label>
                    <input
                        disabled={!canEdit}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="np. Magistrala ul. Polna"
                        style={{
                            width: '100%', backgroundColor: '#000', border: '1px solid #333',
                            color: 'white', padding: '7px 10px', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px'
                        }}
                    />
                </div>

                {/* Pole: Opis */}
                <div>
                    <label style={{ fontSize: '11px', color: '#888', display: 'block', marginBottom: '3px' }}>Opis / Ważne informacje</label>
                    <textarea
                        disabled={!canEdit}
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        placeholder="Dodatkowe informacje techniczne..."
                        rows={1}
                        style={{
                            width: '100%', backgroundColor: '#000', border: '1px solid #333',
                            color: 'white', padding: '7px 10px', borderRadius: '6px', boxSizing: 'border-box', resize: 'vertical', minHeight: '34px', fontSize: '13px'
                        }}
                    />
                </div>

                {/* Sekcja Hierarchii: Wypływa z */}
                <div style={{ padding: '8px 10px', backgroundColor: '#262626', borderRadius: '8px', border: '1px solid #333' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9ca3af', fontSize: '11px' }}>
                                <GitBranch size={13} />
                                <span>Wypływa z:</span>
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>
                                {parentId ? `Rura ID: ${parentId}` : 'Brak (Główna)'}
                            </div>
                        </div>
                        {canEdit && (
                            <button
                                onClick={onStartPickingParent}
                                style={{ backgroundColor: '#333', border: '1px solid #444', color: 'white', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}
                            >
                                {parentId ? 'Zmień' : 'Podepnij'}
                            </button>
                        )}
                    </div>
                </div>

                <div style={{ padding: '8px 10px', backgroundColor: '#262626', borderRadius: '8px', border: '1px solid #333' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={isSp} onChange={e => setIsSp(e.target.checked)} disabled={!canEdit} />
                        <span style={{ fontSize: '13px', color: 'white', fontWeight: 600 }}>SPINKA RURA?</span>
                    </label>
                </div>
            </div>

            {canEdit && (
                <button
                    onClick={() => {
                        onUpdate(pipe.id, { name, description, parent_id: parentId, is_sp: isSp });
                        onClose();
                    }}
                    style={{
                        width: '100%', marginTop: '14px', padding: '9px 12px', borderRadius: '8px',
                        backgroundColor: '#2563eb', color: 'white', border: 'none',
                        cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', gap: '6px'
                    }}
                >
                    <Save size={15} /> Zapisz zmiany
                </button>
            )}
        </div>
    );
};

export default PipeModal;